import React, { useMemo, useRef, useState } from "react";
import ContentPasteGo from "@mui/icons-material/ContentPasteGo";
import { Box, Chip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Alert } from "@components/Alert/Alert";
import Badge from "@components/Badge/Badge";
import Button from "@components/Button/Button";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import Paper from "@components/Paper/Paper";
import Subheader from "@components/Subheader/Subheader";
import { queryClient } from "@config/queryClient";
import { useNotification } from "@context/NotificationContext";
import {
  buildLeftDetailsFields,
  buildRightDetailsFields,
  isDateOnOrAfterToday,
  isDateOnOrBeforeToday,
} from "@features/vessel-call/vessel-call-status/utils";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import type { FromToLocation } from "@services/vessel-call/types";
import type { Interruption } from "@services/vesselCall/types";
import { deleteDraftVesselCall, getVesselCallInterruption } from "@services/vesselCallApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { formatDateToDDMMYYYY } from "@utils/date-time";
import { paths } from "@utils/navigation";
import { checkPermission } from "@utils/user-permissions";
import { t } from "i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import RejectResumptionDrawer from "./flow/reject-resumption/RejectResumptionDrawer";
import ResumptionVesselCallDrawer from "./flow/resumption/ResumptionVesselCallDrawer";
import VesselCallFlowButtons from "./flow/VesselCallFlowButtons";
import { useCompleteResumption } from "./hooks/useCompleteResumption";
import InterruptionSideDrawer from "./InterruptionSideDrawer";
import type { BadgeType } from "./MovementServices/preview/components/MovementServiceSummary";
import type { VesselCallError, VesselHeaderDTO } from "./VesselCallStatus.types";

type VesselCallHeaderProps = {
  vesselDetails: VesselHeaderDTO | null;
  openDrawer: (drawer: string) => void;
  refetchVesselCall: () => Promise<unknown>;
  refetchMovements: () => Promise<unknown>;
  statusOverrideLabel?: string;
  fakeOverrideStatus?: string;
  setFakeOverrideStatus: (status: string) => void;
  replaceFakeStatus: string | undefined;
  showUpdateVesselCall?: () => void;
  showUpdateButton?: boolean;
  setFakeStatus: React.Dispatch<React.SetStateAction<string | null | undefined>>;
  statusOverrideBadgeType?: BadgeType;
  draftExpiresAt?: string;
  canCloseVesselCall?: boolean;
  hasActiveDispute?: boolean;
  onViewDisputes?: () => void;
  movementStatus: string | undefined;
  hasCancellationDetails?: boolean;
};

const serviceVesselCall = "bff-vessel-call";
const INTERRUPTION_STATUSES = [
  "INTERRUPTED_IN_PORT",
  "INTERRUPTED_LEAVE_PORT",
  "RESUME_PENDING",
] as const;

const VesselCallHeader: React.FC<VesselCallHeaderProps> = ({
  vesselDetails,
  openDrawer,
  refetchVesselCall,
  refetchMovements,
  statusOverrideLabel,
  fakeOverrideStatus,
  setFakeOverrideStatus,
  showUpdateVesselCall,
  showUpdateButton = true,
  setFakeStatus,
  statusOverrideBadgeType,
  draftExpiresAt,
  canCloseVesselCall = false,
  hasActiveDispute = false,
  onViewDisputes,
  movementStatus,
  hasCancellationDetails = false,
}) => {
  const theme = useTheme();
  const [interruptionDrawer, setInterruptionDrawer] = useState(false);
  const [openResumptionDrawer, setOpenResumptionDrawer] = useState(false);
  const [openRejectResumptionDrawer, setOpenRejectResumptionDrawer] = useState(false);
  const [isResumeConfirmOpen, setIsResumeConfirmOpen] = useState(false);
  const [isRejectResumeConfirmOpen, setIsRejectResumeConfirmOpen] = useState(false);
  const [isConfirmDiscardDraftOpen, setIsConfirmDiscardDraftOpen] = useState(false);
  const [externalAttachmentsUrl, setExternalAttachmentsUrl] = useState<string>();
  const [externalAttachmentsName, setExternalAttachmentsName] = useState<string>();
  const [internalAttachmentsUrl, setInternalAttachmentsUrl] = useState<string>();
  const [internalAttachmentsName, setInternalAttachmentsName] = useState<string>();
  type ResumePendingPayload = {
    internalNotes: string;
    externalNotes: string;
    internalAttachmentUrl?: string;
    internalAttachmentName?: string;
    externalAttachmentUrl?: string;
    externalAttachmentName?: string;
    forceLeave?: boolean;
  };

  const resumePendingPayloadRef = useRef<ResumePendingPayload | null>(null);
  type RejectResumePendingPayload = {
    reason?: string;
    internalNotes?: string;
    externalNotes?: string;
    internalAttachmentUrl?: string;
    internalAttachmentName?: string;
    externalAttachmentUrl?: string;
    externalAttachmentName?: string;
    forceLeave?: boolean;
  };
  const rejectResumePendingPayloadRef = useRef<RejectResumePendingPayload | null>(null);

  const vesselCallId = vesselDetails?.vesselCallId;
  const status = vesselDetails?.status;

  const isInterruptionStatus = useMemo(() => {
    return INTERRUPTION_STATUSES.includes((status ?? "") as (typeof INTERRUPTION_STATUSES)[number]);
  }, [status]);

  const { data: cachedInterruptions, refetch: refetchInterruption } = useQuery<Interruption>({
    queryKey: ["vesselInterruptions", serviceVesselCall, vesselCallId],
    queryFn: () => getVesselCallInterruption(serviceVesselCall, String(vesselCallId)),
    enabled: Boolean(vesselCallId) && isInterruptionStatus,
    refetchInterval: 60_000,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const invalidateInterruption = async () => {
    if (!id) return;
    await queryClient.invalidateQueries({
      queryKey: ["vesselInterruptions", serviceVesselCall, String(id)],
    });
  };

  const handleResumptionSuccess = async () => {
    await refetchVesselCall();
    await refetchInterruption();
    await refetchMovements();
  };

  const handleResumptiomError = async (error: VesselCallError) => {
    await refetchVesselCall();
    await refetchInterruption();
    notify({
      title: t("common.error"),
      description:
        error?.response?.data?.detail ?? error?.message ?? t("common.something-went-wrong"),
      type: "error",
    });
  };

  const { completeResumptionAsync } = useCompleteResumption({
    handleSuccess: handleResumptionSuccess,
    handleError: handleResumptiomError,
  });

  const documentUploadMutation = useMutation({
    mutationFn: ({ file, documentType }: { file: File; documentType: string }) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "VesselCallHeader",
          action: "upload_vessel_call_document",
          entityType: "vessel_call_document",
        },
        async (transactionId) => {
          const sasPayload = {
            prefix: SasTokenPrefix.VESSEL_REGISTRATION,
            contentType: file.type || null,
            privilege: SasTokenPrivilege.WRITE,
            blobUrl: null,
          };

          const { blobUrl, sasToken } = await getSasToken(
            sasPayload,
            ...(transactionId ? [transactionId] : []),
          );
          const [uploadResult] = await uploadFileToAzureBlob({
            file,
            blobUrl,
            sasToken,
            certificateType: documentType,
          });

          const documentUrl = uploadResult?.documentUrl ?? blobUrl;
          return { documentUrl, documentType, documentName: file.name };
        },
      ),
  });

  const handleDocumentUpload = (file: File, documentType: string) => {
    documentUploadMutation.mutate(
      { file, documentType },
      {
        onSuccess: ({ documentUrl, documentType: currentType, documentName }) => {
          if (currentType === "INTERNAL_NOTES") {
            setInternalAttachmentsUrl(documentUrl);
            setInternalAttachmentsName(documentName);
          }
          if (currentType === "EXTERNAL_NOTES") {
            setExternalAttachmentsUrl(documentUrl);
            setExternalAttachmentsName(documentName);
          }
        },
      },
    );
  };

  const discardDraftMutation = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "VesselCallHeader",
          action: "discard_vessel_call_draft",
          entityType: "vessel_call",
        },
        (transactionId) => deleteDraftVesselCall(id, ...(transactionId ? [transactionId] : [])),
      ),
    onSuccess: async () => {
      notify({
        title: t("vessel-call.notifications.discard-draft-success-title"),
        description: t("vessel-call.notifications.discard-draft-success-description"),
        type: "success",
      });
      setIsConfirmDiscardDraftOpen(false);

      if (vesselCallId) {
        await queryClient.cancelQueries({
          queryKey: ["vesselCall", serviceVesselCall, vesselCallId],
          exact: true,
        });
      }

      navigate(`/${paths.vesselCall.index}`, { replace: true });

      void queryClient.invalidateQueries({
        queryKey: ["vesselCall"],
        refetchType: "none",
      });
    },
    onError: (error: any) => {
      notify({
        title: t("vessel-call.notifications.discard-draft-error-title"),
        description: error?.response?.data?.detail ?? error.message ?? t("common.try-again-later"),
        type: "error",
      });
    },
  });

  const { userAccessPermissions, userActionPermissions, user } = useAuthStore();
  const isMarineDepartment = checkPermission(userAccessPermissions, "hasTaskAccess");

  const navigate = useNavigate();
  const { notify } = useNotification();

  const { id } = useParams<{ id: string }>();

  const hasInformationInViewDetails = Boolean(
    vesselDetails?.lastTaskDecision?.externalNotes ||
      vesselDetails?.lastTaskDecision?.internalNotes ||
      vesselDetails?.lastTaskDecision?.rejectionReason ||
      vesselDetails?.lastTaskDecision?.externalAttachmentsUrl ||
      vesselDetails?.lastTaskDecision?.internalAttachmentsUrl ||
      vesselDetails?.lastTaskDecision?.rejectionAttachmentsUrl ||
      vesselDetails?.status === "INTERRUPTED_IN_PORT" ||
      vesselDetails?.status === "INTERRUPTED_LEAVE_PORT" ||
      vesselDetails?.status === "RESUME_PENDING" ||
      (vesselDetails?.status === "CANCELLED" && hasCancellationDetails),
  );

  const canSeeViewDetails =
    (isMarineDepartment && hasInformationInViewDetails) ||
    (hasInformationInViewDetails && user?.companyId === vesselDetails?.companyId);

  const viewDetailsLink = (
    <Link
      to="#"
      onClick={(e) => {
        e.preventDefault();
        if (!isInterruptionStatus) {
          openDrawer("statusDrawer");
          return;
        }

        setInterruptionDrawer(true);
      }}
      className="font-medium text-blue-800 hover:underline cursor-pointer"
    >
      {t("vessel-call.vessel-call-status-header.view-details")}
    </Link>
  );

  const { options: locations } = useFormattedReference<FromToLocation>(
    REFERENCE_DATA_TYPES.FROM_TO_LOCATIONS,
    "bff-vessel-call",
  );

  const leftDetailsFields = buildLeftDetailsFields({
    isMarineDepartment,
    vesselDetails,
    locations: locations ?? [],
    canSeeViewDetails,
    viewDetailsLink,
    BadgeComponent: (props) => <Badge {...props} label={t(props.label)} />,
    statusOverrideLabel,
    fakeOverrideStatus,
    statusOverrideBadgeType,
    theme,
    t,
  });

  const rightDetailsFields = buildRightDetailsFields({
    vesselDetails,
  });
  let vesselCallFlowData;

  if (vesselDetails) {
    const {
      vesselCallId,
      task,
      status,
      companyId,
      originalVesselCallId,
      hasMovementAnchorTimeOrAllFast,
    } = vesselDetails;
    vesselCallFlowData = {
      vesselCallId,
      task,
      status,
      companyId,
      originalVesselCallId,
      hasMovementAnchorTimeOrAllFast,
      roles: user?.roles,
    };
  }

  const handleFlowOpenDrawer = (drawerName: string) => {
    if (drawerName === "resumeDrawer") {
      setOpenResumptionDrawer(true);
      return;
    }
    if (drawerName === "rejectResumeDrawer") {
      setOpenRejectResumptionDrawer(true);
      return;
    }
    openDrawer(drawerName);
  };

  const handleResumeDrawerSubmit = (payload?: {
    forceLeave: boolean;
    internalNotes: string;
    externalNotes: string;
    internalAttachmentUrl?: string;
    internalAttachmentName?: string;
    externalAttachmentUrl?: string;
    externalAttachmentName?: string;
  }) => {
    if (payload) {
      resumePendingPayloadRef.current = {
        internalNotes: payload.internalNotes,
        externalNotes: payload.externalNotes,
        internalAttachmentUrl: payload.internalAttachmentUrl,
        internalAttachmentName: payload.internalAttachmentName,
        externalAttachmentUrl: payload.externalAttachmentUrl,
        externalAttachmentName: payload.externalAttachmentName,
        forceLeave: payload.forceLeave,
      };
    } else {
      resumePendingPayloadRef.current = null;
    }

    setOpenResumptionDrawer(false);
    setIsResumeConfirmOpen(true);
  };

  const handleRejectResumptionDrawerSubmit = (payload?: {
    internalNotes?: string;
    externalNotes?: string;
    internalAttachmentUrl?: string;
    internalAttachmentName?: string;
    externalAttachmentUrl?: string;
    externalAttachmentName?: string;
    forceLeave?: boolean;
  }) => {
    rejectResumePendingPayloadRef.current = payload ?? null;
    setOpenRejectResumptionDrawer(false);
    setIsRejectResumeConfirmOpen(true);
  };

  const handleCompleteResumption = ({
    status: resumptionStatus,
    internalNotes,
    externalNotes,
    internalAttachmentUrl,
    internalAttachmentName,
    externalAttachmentUrl,
    externalAttachmentName,
    isVesselForcedToLeavePort,
  }: {
    status: string;
    internalNotes?: string;
    externalNotes?: string;
    internalAttachmentUrl?: string;
    internalAttachmentName?: string;
    externalAttachmentUrl?: string;
    externalAttachmentName?: string;
    isVesselForcedToLeavePort?: boolean;
  }) => {
    if (!id) return;

    const params: {
      status: string;
      internalNotes?: string;
      externalNotes?: string;
      internalAttachmentsUrl?: string;
      internalAttachmentsName?: string;
      externalAttachmentsUrl?: string;
      externalAttachmentsName?: string;
      rejectionReason?: string;
      isVesselForcedToLeavePort?: boolean;
    } = {
      status: resumptionStatus,
      internalNotes,
      externalNotes,
      internalAttachmentsUrl: internalAttachmentUrl,
      internalAttachmentsName: internalAttachmentName,
      externalAttachmentsUrl: externalAttachmentUrl,
      externalAttachmentsName: externalAttachmentName,
    };

    if (resumptionStatus !== "APPROVED") {
      params.isVesselForcedToLeavePort =
        isVesselForcedToLeavePort ?? cachedInterruptions?.isVesselForcedToLeavePort ?? false;
    }

    return completeResumptionAsync({
      service: serviceVesselCall,
      vesselCallId: id,
      params,
    });
  };

  const handleConfirmResume = async () => {
    const pending = resumePendingPayloadRef.current;

    try {
      await handleCompleteResumption({
        status: "APPROVED",
        internalNotes: pending?.internalNotes ?? "",
        externalNotes: pending?.externalNotes ?? "",
        internalAttachmentUrl: pending?.internalAttachmentUrl ?? internalAttachmentsUrl,
        internalAttachmentName: pending?.internalAttachmentName ?? internalAttachmentsName,
        externalAttachmentUrl: pending?.externalAttachmentUrl ?? externalAttachmentsUrl,
        externalAttachmentName: pending?.externalAttachmentName ?? externalAttachmentsName,
      });
      setFakeStatus?.(cachedInterruptions?.vesselCallStatusAtInterruption ?? "APPROVED");
    } catch (error) {
      console.log(error);
    }

    setIsResumeConfirmOpen(false);
    invalidateInterruption();
    resumePendingPayloadRef.current = null;
  };

  const handleConfirmRejectResumption = async () => {
    const pending = rejectResumePendingPayloadRef.current;
    try {
      await handleCompleteResumption({
        status: "REJECTED",
        internalNotes: pending?.internalNotes ?? "",
        externalNotes: pending?.externalNotes ?? "",
        internalAttachmentUrl: pending?.internalAttachmentUrl ?? internalAttachmentsUrl,
        internalAttachmentName: pending?.internalAttachmentName ?? internalAttachmentsName,
        externalAttachmentUrl: pending?.externalAttachmentUrl ?? externalAttachmentsUrl,
        externalAttachmentName: pending?.externalAttachmentName ?? externalAttachmentsName,
        isVesselForcedToLeavePort: pending?.forceLeave,
      });
      setFakeOverrideStatus(pending?.forceLeave ? "INTERRUPTED_LEAVE_PORT" : "INTERRUPTED_IN_PORT");
    } catch (error) {
      console.log(error);
    }

    setIsRejectResumeConfirmOpen(false);
    invalidateInterruption();
    rejectResumePendingPayloadRef.current = null;
  };

  const handleConfirmDiscardDraft = async () => {
    if (!vesselCallId) {
      setIsConfirmDiscardDraftOpen(false);
      return;
    }

    await discardDraftMutation.mutateAsync({ id: vesselCallId });
  };

  const handleSubmitVesselCall = () => {
    if (!id) return;

    const target = `/${paths.vesselCall.index}/create/pre-arrival-notification?prefillFromId=${id}&finalSubmission=true`;

    navigate(target, {
      state: {
        prefillFromId: id,
        finalSubmission: true,
      },
    });
  };

  const handleSubmitVesselCallUpdate = () => {
    if (!id) return;

    const target = `/${paths.vesselCall.index}/create/pre-arrival-notification?prefillFromId=${id}`;

    navigate(target, {
      state: {
        prefillFromId: id,
      },
    });
  };

  const showBunkerBarge =
    vesselDetails?.isBunkerBarge || isDateOnOrBeforeToday(vesselDetails?.bunkerBargeStartDate);
  const handleUpdateVesselCall = () => {
    if (showUpdateVesselCall) {
      showUpdateVesselCall();
    }
  };
  return (
    <Subheader
      title={
        <div className="flex items-end justify-between w-full">
          <div className="flex gap-4 items-center">
            <div>
              {vesselDetails?.imoNumber} - {vesselDetails?.vesselName}
            </div>
            {vesselDetails?.isDiplomaticVessel && (
              <Chip
                label={t("vessel-management.diplomatic-vessel")}
                sx={{ bgcolor: "#6d77b8", color: theme.palette.text.white, height: "20px" }}
                size="small"
              />
            )}
            {showBunkerBarge && isDateOnOrAfterToday(vesselDetails?.bunkerBargeGracePeriod) && (
              <Chip
                label={t("vessel-management.bunker-barge")}
                sx={{ bgcolor: "#B15FC0", color: theme.palette.text.white, height: "20px" }}
                size="small"
              />
            )}
          </div>
          <VesselCallFlowButtons
            vesselCallFlowData={
              vesselCallFlowData
                ? {
                    ...vesselCallFlowData,
                    companyId: vesselCallFlowData.companyId ?? "",
                    movementStatus: movementStatus,
                  }
                : undefined
            }
            openDrawer={handleFlowOpenDrawer}
            refetchVesselCall={refetchVesselCall}
            refetchInterruption={refetchInterruption}
            onUpdateVesselCall={handleSubmitVesselCallUpdate}
            showUpdateButton={showUpdateButton}
            onUpdateEta={handleUpdateVesselCall}
            setIsConfirmDiscardDraftOpen={setIsConfirmDiscardDraftOpen}
            interruptionTaskStatus={cachedInterruptions?.task?.status}
            claimOwnerName={cachedInterruptions?.task?.claimOwnerName}
            canCloseVesselCall={canCloseVesselCall}
          />
        </div>
      }
      subtitle={
        <div className="flex items-center space-x-2 text-sm text-gray-700">
          <Link to="/vessel-call" className="text-blue-600 hover:underline">
            {t("navigation.vessel-call")}
          </Link>
          <span className="text-gray-500">/</span>
          <span className="text-gray-500">
            {vesselDetails?.imoNumber} - {vesselDetails?.vesselName}
          </span>
        </div>
      }
    >
      {hasActiveDispute && (
        <Box className="-mx-24 py-3">
          <Alert type="warning" className="py-3">
            <div className="text-sm text-gray-800">
              <span>{t("vessel-call.vessel-call-status-header.vessel-has-disputes")}</span>
              {onViewDisputes && (
                <>
                  <span> - </span>
                  <Link
                    to="#"
                    onClick={(event) => {
                      event.preventDefault();
                      onViewDisputes();
                    }}
                    className="font-bold text-blue-800 hover:underline"
                    data-testid="vessel-call-view-disputes"
                  >
                    {t("vessel-call.vessel-call-status-header.view-disputes")}
                  </Link>
                </>
              )}
            </div>
          </Alert>
        </Box>
      )}
      {draftExpiresAt && (
        <Box className="-mx-24 pt-6 pb-5">
          <Alert type="info">
            {t(`common.draft-info`, {
              date: formatDateToDDMMYYYY(draftExpiresAt),
            })}
          </Alert>
        </Box>
      )}
      {vesselDetails?.status === "PRE_APPROVED" &&
        checkPermission(userActionPermissions, "canSubmitFinalVesselCall") &&
        user?.companyId === vesselDetails.companyId && (
          <div className="bg-[#E9F0FF] mt-3">
            <div className="flex items-center justify-between w-full px-3 py-3">
              <span>{t("vessel-call.vessel-call-status-header.preapproved-banner-text")}</span>
              <Button
                variant="contained"
                startIcon={<ContentPasteGo />}
                className="whitespace-nowrap"
                onClick={handleSubmitVesselCall}
                label={
                  <span className="normal-case font-medium">
                    {t("vessel-call.vessel-call-status-header.submit-final-vessel-call-button")}
                  </span>
                }
              />
            </div>
          </div>
        )}
      <div className="mt-3 flex gap-4">
        <div className="basis-80 h-full rounded-2xl overflow-hidden">
          <Paper variant="outlined">
            <div className={`grid gap-y-5 px-3 ${canSeeViewDetails ? "py-1" : "py-3"}`}>
              {leftDetailsFields.map((f, idx) => (
                <div
                  key={typeof f.label === "string" ? f.label : `left-field-${idx}`}
                  className="flex flex-col"
                >
                  <span className="text-xs font-medium text-gray-500">
                    {typeof f.label === "string" ? t(f.label) : f.label || null}
                  </span>
                  {f.value}
                </div>
              ))}
            </div>
          </Paper>
        </div>
        <div className="flex-1 h-full rounded-2xl overflow-hidden">
          <Paper variant="outlined">
            <div className="grid grid-cols-4 gap-y-5 gap-x-10 h-full px-4 py-3">
              {rightDetailsFields.map((f, idx) => (
                <div
                  key={typeof f.label === "string" ? f.label : `right-field-${idx}`}
                  className="flex flex-col"
                >
                  <span className="text-xs font-medium text-gray-500">
                    {typeof f.label === "string" ? t(f.label) : f.label || null}
                  </span>
                  <span className="text-sm font-bold wrap-break-word">{f.value}</span>
                </div>
              ))}
              <div />
              <div />
            </div>
          </Paper>
        </div>
      </div>
      <InterruptionSideDrawer
        isOpen={interruptionDrawer}
        onClose={() => setInterruptionDrawer(false)}
        status={vesselDetails?.status}
        fakeOverrideStatus={fakeOverrideStatus}
        submittedBy={cachedInterruptions?.submittedBy}
        submittedByRole={cachedInterruptions?.submittedByRole}
        createdAt={cachedInterruptions?.createdAt}
        rejectedBy={
          cachedInterruptions?.rejectedBy ?? cachedInterruptions?.lastTaskDecision?.submittedBy
        }
        rejectedByRole={
          cachedInterruptions?.rejectedByRole ??
          cachedInterruptions?.lastTaskDecision?.submittedByRole
        }
        rejectedAt={
          cachedInterruptions?.rejectedAt ?? cachedInterruptions?.lastTaskDecision?.timestamp
        }
        resumedBy={
          cachedInterruptions?.resumedBy ?? cachedInterruptions?.lastTaskDecision?.submittedBy
        }
        resumedByRole={
          cachedInterruptions?.resumedByRole ??
          cachedInterruptions?.lastTaskDecision?.submittedByRole
        }
        resumedAt={
          cachedInterruptions?.resumedAt ?? cachedInterruptions?.lastTaskDecision?.timestamp
        }
        externalNotesInterruption={cachedInterruptions?.externalNotes}
        externalAttachmentsUrlInterruption={cachedInterruptions?.externalAttachmentsUrl}
        externalAttachmentsNameInterruption={cachedInterruptions?.externalAttachmentsName}
        rejectionExternalNotes={cachedInterruptions?.rejectionExternalNotes}
        rejectionExternalAttachmentsUrl={cachedInterruptions?.rejectionExternalAttachmentsUrl}
        rejectionExternalAttachmentsName={cachedInterruptions?.rejectionExternalAttachmentsName}
        rejectionInternalNotes={cachedInterruptions?.rejectionInternalNotes}
        rejectionInternalAttachmentsUrl={cachedInterruptions?.rejectionInternalAttachmentsUrl}
        rejectionInternalAttachmentsName={cachedInterruptions?.rejectionInternalAttachmentsName}
        internalNotesInterruption={cachedInterruptions?.internalNotes}
        internalAttachmentsUrlInterruption={cachedInterruptions?.internalAttachmentsUrl}
        internalAttachmentsNameInterruption={cachedInterruptions?.internalAttachmentsName}
        externalNotes={cachedInterruptions?.lastTaskDecision?.externalNotes}
        externalAttachmentsUrl={cachedInterruptions?.lastTaskDecision?.externalAttachmentsUrl}
        externalAttachmentsName={cachedInterruptions?.lastTaskDecision?.externalAttachmentsName}
        internalNotes={cachedInterruptions?.lastTaskDecision?.internalNotes}
        internalAttachmentsUrl={cachedInterruptions?.lastTaskDecision?.internalAttachmentsUrl}
        internalAttachmentsName={cachedInterruptions?.lastTaskDecision?.internalAttachmentsName}
        lastTaskStatus={
          cachedInterruptions?.lastTaskDecision?.status
            ? cachedInterruptions?.lastTaskDecision?.status
            : null
        }
      />

      <ResumptionVesselCallDrawer
        isOpen={openResumptionDrawer}
        onClose={() => setOpenResumptionDrawer(false)}
        internalAttachmentUrl={internalAttachmentsUrl}
        internalAttachmentName={internalAttachmentsName}
        externalAttachmentUrl={externalAttachmentsUrl}
        externalAttachmentName={externalAttachmentsName}
        onDocumentUpload={(file) => handleDocumentUpload(file, "EXTERNAL_NOTES")}
        onAdditionalDocumentUpload={(file) => handleDocumentUpload(file, "INTERNAL_NOTES")}
        onSubmit={handleResumeDrawerSubmit}
      />

      <RejectResumptionDrawer
        isOpen={openRejectResumptionDrawer}
        onClose={() => setOpenRejectResumptionDrawer(false)}
        internalAttachmentUrl={internalAttachmentsUrl}
        internalAttachmentName={internalAttachmentsName}
        externalAttachmentUrl={externalAttachmentsUrl}
        externalAttachmentName={externalAttachmentsName}
        onDocumentUpload={(file) => handleDocumentUpload(file, "EXTERNAL_NOTES")}
        onAdditionalDocumentUpload={(file) => handleDocumentUpload(file, "INTERNAL_NOTES")}
        onSubmit={handleRejectResumptionDrawerSubmit}
      />

      <GenericDialog
        open={isResumeConfirmOpen}
        title={t("vessel-call.resume.confirm-title")}
        content=""
        cancelButtonText={t("common.cancel")}
        confirmButtonText={t("vessel-call.resume.submit", { defaultValue: "Resume" })}
        handleCloseDialog={() => setIsResumeConfirmOpen(false)}
        confirmAction={handleConfirmResume}
        testId="resume-vessel-call-confirm-dialog"
        maxWidth="sm"
      />

      <GenericDialog
        open={isRejectResumeConfirmOpen}
        title={t("vessel-call.resumption.reject-confirm-title")}
        content=""
        cancelButtonText={t("common.cancel")}
        confirmButtonText={t("vessel-call.resumption.reject.submit", { defaultValue: "Reject" })}
        handleCloseDialog={() => setIsRejectResumeConfirmOpen(false)}
        confirmAction={handleConfirmRejectResumption}
        testId="reject-resumption-confirm-dialog"
        maxWidth="md"
      />

      <GenericDialog
        open={isConfirmDiscardDraftOpen}
        title={t("vessel-call.discard-draft.confirm-title")}
        content={t("vessel-call.discard-draft.description")}
        cancelButtonText={t("common.cancel")}
        confirmButtonText={t("vessel-call.discard-draft.submit")}
        handleCloseDialog={() => setIsConfirmDiscardDraftOpen(false)}
        confirmAction={handleConfirmDiscardDraft}
        testId="discard-draft-confirm-dialog"
        maxWidth="sm"
      />
    </Subheader>
  );
};

export default VesselCallHeader;
